import { useEffect, useRef, useState } from 'react'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */

/** API */
import DiagnosisApi from 'api/setting/treatments/diagnosis.api'
import { routeName } from 'routes/routes-name'

/** UTILS || SLICE */
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import DiagnosisTypeApi from 'api/setting/treatments/diagnosis-type.api'
import HeaderCard from 'component/CardCustom/HeaderCard'
import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import { notiError } from 'component/notifications/notifications'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import { dateTimeToView } from 'utils/date.utils'
import { exportDiagnosisToExcel } from './exportDiagnosisToExcel'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

type DiaTypeProps = {
  diagnosisTypeId: number
  diagnosisTypeName: string
  diagnosisTypeNameEn: string
  status: 'ACTIVE' | 'INACTIVE'
}
export default function Diagnosis() {
  const { t } = useTranslation()
  const componentRef = useRef<any>(null)
  const [diagnosisTypes, setDiagnosis] = useState([])
  const [exportData, setExportData] = useState([])

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('diagnosisCode')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [diagnosisTypesOp, setDiagnosisTypesOp] = useState<DiaTypeProps[]>([])

  const loadDiagnosisTypes = async (search = '') => {
    const condition = { search: search, pageLimit: 50, status: 'ACTIVE' }
    const respDiagnosisTypes = await DiagnosisTypeApi.findAll(condition)
    if (respDiagnosisTypes.status === 200) setDiagnosisTypesOp(respDiagnosisTypes.data)
  }

  useEffect(() => {
    loadDiagnosisTypes()
  }, [])

  const loadData = async () => {
    try {
      let condition = {}
      condition = {
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      }
      const resp = await DiagnosisApi.findAll(condition)
      const respExport = await DiagnosisApi.findAll({ ...condition, export: 1 })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setDiagnosis(resp.data)
        setExportData(respExport.data)
      } else {
        setDiagnosis([])
        setExportData([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])


  const onCreate = () => {
    window.location.href = routeName.diagnosis + '/create'
  }
  const onActive = () => {
    if (id) {
      DiagnosisApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('DIAGNOSIS.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('DIAGNOSIS.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = async () => {
    if (id) {
      DiagnosisApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('DIAGNOSIS.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('DIAGNOSIS.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    window.location.href = routeName.diagnosis + `/${data.diagnosisId}`
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const headCells = [
    { id: 'no', disablePadding: false, label: t('TABLE.NO') },
    { id: 'diagnosisCode', disablePadding: false, label: t('DIAGNOSIS.TABLE.CELL.DIAGNOSIS_CODE'), align: 'center' },
    { id: 'diagnosisType', disablePadding: false, label: t('DIAGNOSIS.INPUT.DIAGNOSIS_TYPE') },
    { id: 'diagnosisName', disablePadding: false, label: t('DIAGNOSIS.TABLE.CELL.DIAGNOSIS_NAME') },
    { id: 'diagnosisRecommends', disablePadding: false, label: t('DIAGNOSIS.TABLE.CELL.DIAGNOSIS_RECOMMEND') },
    { id: 'status', disablePadding: false, label: t('DIAGNOSIS.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('DIAGNOSIS.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number, valueExportData: boolean) => {
    const num = valueExportData === true ? no + 1 : page * pageLimit - pageLimit + no + 1
    const { diagnosisId, diagnosisName, updatedBy, updatedAt, status: dgoStatus, diagnosisRecommends, diagnosisCode, diagnosisTypeId } = objData
    const statusBtnActive = dgoStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = dgoStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled

    const [diaType]: DiaTypeProps[] = diagnosisTypesOp.filter((item: DiaTypeProps) => item?.diagnosisTypeId === diagnosisTypeId)

    const objRenderData = {
      key: diagnosisId,
      id: diagnosisId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: num },
        { option: 'TEXT', align: 'center', label: diagnosisCode, },
        { option: 'TEXT', align: 'left', label: diaType?.diagnosisTypeName || diagnosisTypeId },
        { option: 'TEXT', align: 'left', label: diagnosisName },
        { option: 'TEXT', align: 'center', label: diagnosisRecommends ? diagnosisRecommends.length : 0 },
        { option: 'STATUS', align: 'center', label: dgoStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <>
        {valueExportData === true && <TableRowExport {...objRenderData} /> ||
          <TableRowCommon
            {...objRenderData}
            onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
            oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
            onedit={() => onEdit(objRenderData.obj)}
            ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
          />
        }
      </>
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.diagnosisName)
  }

  const filter = [
    { label: `${t('INPUT.SEARCH')}: ${search || '-'}` },
    { label: `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}` }
  ]

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`DIAGNOSIS.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`DIAGNOSIS.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { diagnosisName: name, status: t(`DIAGNOSIS.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('DIAGNOSIS.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('DIAGNOSIS.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />

              <Box display="flex" flexDirection="row" alignItems="center" gap="16px">
                <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                  if (e === 'Excel') {
                    const report: any = {
                      title: t('DIAGNOSIS.TITLE'),
                      statusFilter: filter[1].label,
                      rows: exportData.map((item: any) => ({
                        diagnosisCode: item?.diagnosisCode || '-',
                        diagnosisTypeName: diagnosisTypesOp.find((v: DiaTypeProps) => v?.diagnosisTypeId === item?.diagnosisTypeId)?.diagnosisTypeName || '-',
                        diagnosisName: item?.diagnosisName,
                        diagnosisRecommends: item?.diagnosisRecommends ? item?.diagnosisRecommends.length : 0,
                        status: t(`STATUS.${item?.status}`),
                        updatedBy: item?.updatedBy,
                        updatedAt: dateTimeToView(item?.updatedAt)
                      })),
                      t
                    }
                    exportDiagnosisToExcel(report)
                  } else if (e === 'PDF') {
                    const style = document.createElement("style");
                    style.innerHTML = `
                      @media print {
                        @page { size: portrait }
                      }
                    `;
                    document.head.appendChild(style);
                    window.print();
                    document.head.removeChild(style);

                  }
                }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                <MainButton title={t('DIAGNOSIS.BUTTON.ADD')} disabled={permissions.isCreate.disabled} type='primary' variant='contained' onClick={() => onCreate()} startIcon={<AddCircleIcon />} />
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={diagnosisTypes.map((val, i) => {
              return renderData(val, i, false)
            })}
          />

          <Box
            id="print-table-container"
            ref={componentRef}
          >
            <Typography fontSize="14px" fontWeight={400}>{t('DIAGNOSIS.TITLE')}</Typography>
            <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
              <Typography fontSize="10px" fontWeight={400}>{t('STATUS.LABEL')}: {status === 'ALL' ? t('STATUS.ALL') : status === 'ACTIVE' ? t('STATUS.ACTIVE') : t('STATUS.INACTIVE')}</Typography>
            </Box>
            <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
              <thead>
                <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
                  <th style={{ borderBottom: '1px solid black' }}>{t('#')}</th>
                  <th style={{ borderBottom: '1px solid black' }}>{t('DIAGNOSIS.TABLE.CELL.DIAGNOSIS_CODE')}</th>
                  <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('DIAGNOSIS.INPUT.DIAGNOSIS_TYPE')}</th>
                  <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('DIAGNOSIS.TABLE.CELL.DIAGNOSIS_NAME')}</th>
                  <th style={{ borderBottom: '1px solid black' }}>{t('DIAGNOSIS.TABLE.CELL.DIAGNOSIS_RECOMMEND')}</th>
                  <th style={{ borderBottom: '1px solid black' }}>{t('DIAGNOSIS.TABLE.CELL.STATUS')}</th>
                  <th style={{ borderBottom: '1px solid black' }}>{t('DIAGNOSIS.TABLE.CELL.UPDATED_BY')}</th>
                </tr>
              </thead>
              <tbody>
                {exportData?.length === 0 &&
                  <tr>
                    <td colSpan={7} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
                  </tr>
                }
                {exportData.map((data: any, index: number) => (
                  <tr key={index}>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '85px' }}>{data?.diagnosisCode || '-'}</td>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '100px' }}>{diagnosisTypesOp.find((item: DiaTypeProps) => item?.diagnosisTypeId === data?.diagnosisTypeId)?.diagnosisTypeName || '-'}</td>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '250px' }}>{data?.diagnosisName || '-'}</td>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100px' }}>{data?.diagnosisRecommends ? data?.diagnosisRecommends.length : 0}</td>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '85px' }}>{t(`STATUS.${data?.status}`) || '-'}</td>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                      <span>{data?.updatedBy || '-'}</span>
                      <span>{dateTimeToView(data?.updatedAt) || '-'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
